import {
  Block,
  Button,
  FlexContainer,
  InputCheckbox,
  InputSelect,
  Loader,
  Modal,
  SoundNotificationContext,
  Span,
  Tabs,
  useNotifications,
} from "@uni-space/uni-components";
import {useContext, useEffect, useMemo, useState} from "react";
import {useMutation} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import { CreateBagProps, ICreateStateBag, ManifestBagType } from '../model/manualSorting';
import { createBags } from '../api';

const DEFAULT_BAG_STATE: ICreateStateBag = {
  bagCode: "",
  regionZone: "",
  type: ManifestBagType.Bag,
  isReturn: false,
};

export default function CreateBag(props: CreateBagProps) {
  const {
    isLoading,
    onRefetchTable,
    onChangeOpenModal: setOpenModal,
    isOpenModal: openModal,
    bagCodes,
  } = props;

  const {onSoundSuccess, onSoundError} = useContext(SoundNotificationContext);
  const {t} = useTranslation();
  const {addNotification} = useNotifications();

  const handleCloseModalBag = () => {
    setCreateStateBag(DEFAULT_BAG_STATE);
    setOpenModal(false);
  };

  const [createStateBag, setCreateStateBag] = useState<ICreateStateBag>(DEFAULT_BAG_STATE);

  const [regionZone, setRegionZone] = useState<string[]>([]);

  useEffect(() => {
    setRegionZone([...new Set(bagCodes.map((i) => i.regionZone))]);
  }, [bagCodes]);

  const handleChangeCreateState = (key: keyof ICreateStateBag, value: string) => {
    if (key === "regionZone" && value !== createStateBag.regionZone) {
      setCreateStateBag((prev) => ({...prev, bagCode: "", regionZone: value}));
    } else {
      setCreateStateBag((prev) => ({...prev, [key]: value}));
    }
  };

  const {mutateAsync: fetchCreateBag, isPending: isLoadingCreateBag} = useMutation({
    mutationKey: ["createBag"],
    mutationFn: () => {
      return createBags(createStateBag.bagCode, createStateBag.regionZone, createStateBag.type, createStateBag.isReturn);
    },
    onSuccess: () => {
      setOpenModal(false);
      onRefetchTable?.();
      addNotification({type: "success", message: t("bagCreated")});
      setCreateStateBag(DEFAULT_BAG_STATE);
      onSoundSuccess();
    },
    onError: () => {
      onSoundError();
      addNotification({type: "error", message: t("unexpectedError")});
    },
  });

  const createBagHandler = () => {
    fetchCreateBag();
  };

  const filteredBagCode = useMemo(() => {
    if (!createStateBag.regionZone) {
      return [];
    }

    const region = bagCodes.find((item) => item.regionZone === createStateBag.regionZone);

    if (!region) {
      return [];
    }

    return Array.from(new Set(region.bagCodes.map((b) => b.code)));
  }, [bagCodes, createStateBag.regionZone]);

  const selectedSortingCodes = useMemo(() => {
    if (!createStateBag.regionZone || !createStateBag.bagCode) {
      return [];
    }

    const region = bagCodes.find((item) => item.regionZone === createStateBag.regionZone);

    const bag = region?.bagCodes.find((b) => b.code === createStateBag.bagCode);

    return bag?.sortingCodes ?? [];
  }, [bagCodes, createStateBag.regionZone, createStateBag.bagCode]);

  return (
    <Modal width={{max: "550px", min: '350px'}} isOpen={openModal} onClose={handleCloseModalBag}>
      {isLoading ? (
        <FlexContainer justify='center'>
          <Loader size='s' />
        </FlexContainer>
      ) : (
        <FlexContainer style={{position: "relative"}} direction='column' gap='16px'>
          <Block textAlign='center'>
            <Span fs={24}>{t("createBagTitle")}</Span>
          </Block>

          <Tabs
            value={String(createStateBag.type)}
            onChange={(id) =>
              setCreateStateBag((prev) => ({...prev, type: Number(id) as ManifestBagType}))
            }
            items={[
              {id: String(ManifestBagType.Bag), label: t("bagType.bag")},
              {id: String(ManifestBagType.BoxL), label: t("bagType.boxL")},
              {id: String(ManifestBagType.BigBag), label: t("bagType.bigBag")},
              {id: String(ManifestBagType.LowDensityBag), label: t("bagType.lowDensityBag")},
              {id: String(ManifestBagType.BagDG), label: t("bagType.bagDG")},
              {id: String(ManifestBagType.BigBagDG), label: t("bagType.bigBagDG")},
            ]}
          />

          <InputCheckbox
            id='create-bag-is-return'
            label={t('isReturn')}
            checked={createStateBag.isReturn}
            disabled={isLoadingCreateBag}
            onChange={(checked) => setCreateStateBag((prev) => ({...prev, isReturn: checked}))}
          />

          {/* Для возвратного мешка регион и bagCode не указываются */}
          {!createStateBag.isReturn && (
            <>
              <InputSelect
                variant='primary'
                id='RegionZone'
                value={createStateBag.regionZone}
                onChange={(_, __, ___, id) => handleChangeCreateState("regionZone", id)}
                placeholder={t("regionZonePlaceholder")}
                items={regionZone.map((item) => ({id: item, label: `Region ${item}`})) ?? []}
                onClean={() => setCreateStateBag((prev) => ({...prev, bagCode: "", regionZone: ""}))}
              />

              <InputSelect
                variant='primary'
                id='RouteName'
                disabled={!createStateBag.regionZone}
                value={createStateBag.bagCode}
                onChange={(val) => handleChangeCreateState("bagCode", val)}
                placeholder={t("bagCode")}
                items={filteredBagCode.map((i) => ({id: i, label: i}))}
              />
              {selectedSortingCodes.length > 0 && (
                <Block
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    background: "#F5F5F5",
                  }}>
                  <Span fw={500}>{t("sortingCode")}: </Span>
                  <Span>{selectedSortingCodes.join(", ")}</Span>
                </Block>
              )}
            </>
          )}
          <FlexContainer justify='space-between'>
            <Button
              onClick={() => {
                setCreateStateBag(DEFAULT_BAG_STATE);
                setOpenModal(false);
              }}>
              {t("cancel")}
            </Button>
            <Button
              disabled={!createStateBag.isReturn && !createStateBag.bagCode}
              loading={isLoadingCreateBag}
              onClick={createBagHandler}>
              {t("create")}
            </Button>
          </FlexContainer>
        </FlexContainer>
      )}
    </Modal>
  );
}
